/**
 * API response test data builders
 * @module @orchard9ai/testing-utils/builders/api
 */

import { faker } from '@faker-js/faker';
import type {
  ApiResponse,
  ApiErrorResponse,
  ApiError,
  PaginatedResponse,
  PaginationMeta,
  BatchResponse,
  HealthCheckResponse,
  HealthCheck,
} from '@orchard9ai/types';
import { ErrorCode } from '@orchard9ai/types';

/**
 * API response builder
 */
export const apiResponseBuilder = <T = any>() => {
  const response: ApiResponse<T> = {
    data: null as any,
    timestamp: new Date().toISOString(),
  };

  return {
    withData(data: T) {
      response.data = data;
      return this;
    },
    withMeta(meta: Record<string, any>) {
      response.meta = meta;
      return this;
    },
    withTimestamp(timestamp: string) {
      response.timestamp = timestamp;
      return this;
    },
    build(): ApiResponse<T> {
      return { ...response };
    },
  };
};

/**
 * API error builder
 */
export const apiErrorBuilder = () => {
  const error: ApiError = {
    code: faker.helpers.arrayElement(Object.values(ErrorCode)),
    message: faker.lorem.sentence(),
  };

  return {
    withCode(code: string) {
      error.code = code;
      return this;
    },
    withMessage(message: string) {
      error.message = message;
      return this;
    },
    withDetails(details: Record<string, any>) {
      error.details = details;
      return this;
    },
    withFields(fields: Record<string, string[]>) {
      error.fields = fields;
      return this;
    },
    asValidationError() {
      error.code = ErrorCode.VALIDATION_ERROR;
      error.message = 'Validation failed';
      error.fields = {
        email: ['Invalid email format'],
        password: ['Password must be at least 8 characters'],
      };
      return this;
    },
    asNotFoundError() {
      error.code = ErrorCode.NOT_FOUND;
      error.message = 'Resource not found';
      return this;
    },
    asUnauthorizedError() {
      error.code = ErrorCode.UNAUTHORIZED;
      error.message = 'Authentication required';
      return this;
    },
    build(): ApiError {
      return { ...error };
    },
  };
};

/**
 * API error response builder
 */
export const apiErrorResponseBuilder = () => {
  const response: ApiErrorResponse = {
    error: apiErrorBuilder().build(),
    timestamp: new Date().toISOString(),
  };

  return {
    withError(error: ApiError) {
      response.error = error;
      return this;
    },
    withRequestId(requestId: string) {
      response.requestId = requestId;
      return this;
    },
    asValidationError() {
      response.error = apiErrorBuilder().asValidationError().build();
      return this;
    },
    asNotFound() {
      response.error = apiErrorBuilder().asNotFoundError().build();
      return this;
    },
    asUnauthorized() {
      response.error = apiErrorBuilder().asUnauthorizedError().build();
      return this;
    },
    build(): ApiErrorResponse {
      return { ...response };
    },
  };
};

/**
 * Paginated response builder
 */
export const paginatedResponseBuilder = <T = any>() => {
  const defaultMeta: PaginationMeta = {
    total: 100,
    page: 1,
    perPage: 20,
    totalPages: 5,
    hasNext: true,
    hasPrev: false,
  };

  const response: PaginatedResponse<T> = {
    data: [],
    meta: defaultMeta,
    timestamp: new Date().toISOString(),
  };

  return {
    withData(data: T[]) {
      response.data = data;
      return this;
    },
    withTotal(total: number) {
      response.meta.total = total;
      response.meta.totalPages = Math.ceil(total / response.meta.perPage);
      return this;
    },
    withPage(page: number) {
      response.meta.page = page;
      response.meta.hasPrev = page > 1;
      response.meta.hasNext = page < response.meta.totalPages;
      return this;
    },
    withPerPage(perPage: number) {
      response.meta.perPage = perPage;
      response.meta.totalPages = Math.ceil(response.meta.total / perPage);
      return this;
    },
    asFirstPage() {
      response.meta.page = 1;
      response.meta.hasPrev = false;
      response.meta.hasNext = response.meta.totalPages > 1;
      return this;
    },
    asLastPage() {
      response.meta.page = response.meta.totalPages;
      response.meta.hasPrev = response.meta.totalPages > 1;
      response.meta.hasNext = false;
      return this;
    },
    asEmptyResult() {
      response.data = [];
      response.meta.total = 0;
      response.meta.totalPages = 0;
      response.meta.hasNext = false;
      response.meta.hasPrev = false;
      return this;
    },
    build(): PaginatedResponse<T> {
      return { ...response };
    },
  };
};

/**
 * Batch response builder
 */
export const batchResponseBuilder = <T = any>() => {
  const response: BatchResponse<T> = {
    succeeded: [],
    failed: [],
    summary: {
      total: 0,
      succeeded: 0,
      failed: 0,
      duration: 0,
    },
  };

  return {
    withSucceeded(items: Array<{ id: string; result: T }>) {
      response.succeeded = items;
      response.summary.succeeded = items.length;
      response.summary.total = response.succeeded.length + response.failed.length;
      return this;
    },
    withFailed(items: Array<{ id: string; error: ApiError }>) {
      response.failed = items;
      response.summary.failed = items.length;
      response.summary.total = response.succeeded.length + response.failed.length;
      return this;
    },
    withDuration(ms: number) {
      response.summary.duration = ms;
      return this;
    },
    allSucceeded(count: number, resultFactory: () => T) {
      response.succeeded = Array.from({ length: count }, (_, i) => ({
        id: `item-${i}`,
        result: resultFactory(),
      }));
      response.failed = [];
      response.summary = {
        total: count,
        succeeded: count,
        failed: 0,
        duration: faker.number.int({ min: 100, max: 1000 }),
      };
      return this;
    },
    allFailed(count: number) {
      response.succeeded = [];
      response.failed = Array.from({ length: count }, (_, i) => ({
        id: `item-${i}`,
        error: apiErrorBuilder().build(),
      }));
      response.summary = {
        total: count,
        succeeded: 0,
        failed: count,
        duration: faker.number.int({ min: 100, max: 1000 }),
      };
      return this;
    },
    build(): BatchResponse<T> {
      return { ...response };
    },
  };
};

/**
 * Health check response builder
 */
export const healthCheckResponseBuilder = () => {
  const response: HealthCheckResponse = {
    status: 'healthy',
    version: '1.0.0',
    uptime: faker.number.int({ min: 1000, max: 86400 }),
    checks: [],
    timestamp: new Date().toISOString(),
  };

  return {
    withStatus(status: 'healthy' | 'degraded' | 'unhealthy') {
      response.status = status;
      return this;
    },
    withVersion(version: string) {
      response.version = version;
      return this;
    },
    withUptime(seconds: number) {
      response.uptime = seconds;
      return this;
    },
    withCheck(check: HealthCheck) {
      response.checks.push(check);
      return this;
    },
    asHealthy() {
      response.status = 'healthy';
      response.checks = [
        { name: 'database', status: 'pass', duration: 5 },
        { name: 'redis', status: 'pass', duration: 2 },
        { name: 'api', status: 'pass', duration: 10 },
      ];
      return this;
    },
    asDegraded() {
      response.status = 'degraded';
      response.checks = [
        { name: 'database', status: 'pass', duration: 5 },
        { name: 'redis', status: 'warn', message: 'High memory usage', duration: 2 },
        { name: 'api', status: 'pass', duration: 10 },
      ];
      return this;
    },
    asUnhealthy() {
      response.status = 'unhealthy';
      response.checks = [
        { name: 'database', status: 'fail', message: 'Connection timeout', duration: 5000 },
        { name: 'redis', status: 'pass', duration: 2 },
        { name: 'api', status: 'pass', duration: 10 },
      ];
      return this;
    },
    build(): HealthCheckResponse {
      return { ...response };
    },
  };
};
